import { useCallback } from "react";
import dagre from "dagre";

const NODE_WIDTH = 260;
const NODE_HEIGHT = 120;

/**
 * Hook that provides auto-layout functionality using dagre.
 * Returns a function that takes current nodes/edges and returns
 * new nodes with computed positions.
 */
export default function useAutoLayout() {
  const getLayoutedElements = useCallback((nodes, edges, direction = "LR") => {
    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));

    // LR = left-to-right (horizontal flow), TB = top-to-bottom
    g.setGraph({
      rankdir: direction,
      nodesep: 60,
      ranksep: 100,
      edgesep: 30,
      marginx: 40,
      marginy: 40,
    });

    // Add nodes to dagre graph
    nodes.forEach((node) => {
      // Use measured dimensions if available, otherwise defaults
      const w = node.measured?.width || node.width || NODE_WIDTH;
      const h = node.measured?.height || node.height || NODE_HEIGHT;
      g.setNode(node.id, { width: w, height: h });
    });

    // Add edges to dagre graph
    edges.forEach((edge) => {
      g.setEdge(edge.source, edge.target);
    });

    // Run layout
    dagre.layout(g);

    // Apply computed positions (dagre returns center, React Flow uses top-left)
    const layoutedNodes = nodes.map((node) => {
      const nodeWithPosition = g.node(node.id);
      if (!nodeWithPosition) return node;

      const w = node.measured?.width || node.width || NODE_WIDTH;
      const h = node.measured?.height || node.height || NODE_HEIGHT;

      return {
        ...node,
        position: {
          x: nodeWithPosition.x - w / 2,
          y: nodeWithPosition.y - h / 2,
        },
      };
    });

    return layoutedNodes;
  }, []);

  return { getLayoutedElements };
}
